<template>
  <div class="widget-person-card">
    <div class="flex justify-between">
      <div class="flex flex-row items-center gap-3">
        <WidgetIcon v-if="icons[person.type]" :icon="icons[person.type]" height="2.25rem" class="widget-primary-icon" />
        <div>
          <p class="title4-bold">{{ getFullName(person.firstName, person.lastName) }}</p>
          <p class="widget-person-card_show" @click="isShowMore = !isShowMore">
            Show {{ isShowMore ? 'less' : 'more' }}
          </p>
        </div>
      </div>
      <div class="flex flex-row items-center gap-2">
        <WButton type="ghost" @click="emit('edit')">
          <WidgetIcon :icon="editIcon" height="1.5rem" class="widget-action-icon" />
        </WButton>
        <WButton type="ghost" @click="openDeleteModal">
          <WidgetIcon :icon="deleteIcon" height="1.5rem" class="widget-action-icon-negative" />
        </WButton>
      </div>
    </div>
    <HeightTransition :visible="isShowMore">
      <div class="mb-1 mt-4 flex items-center">
        <WidgetIcon :icon="themeIcons.mail" class="widget-person-card_detail--icon" />
        <p class="widget-person-card_detail--text">{{ person.email || 'Unknown' }}</p>
      </div>
      <div class="mb-1 flex items-center">
        <WidgetIcon :icon="themeIcons.phone" class="widget-person-card_detail--icon" />
        <p class="widget-person-card_detail--text">{{ person.phone?.formatted || 'Unknown' }}</p>
      </div>
      <div class="mb-1 flex items-center">
        <WidgetIcon :icon="themeIcons.marker" class="widget-person-card_detail--icon" />
        <p class="widget-person-card_detail--text">{{ getFormattedAddress() }}</p>
      </div>
      <div v-if="!!person.vehicle" class="flex items-center">
        <WidgetIcon :icon="themeIcons.car" class="widget-person-card_detail--icon" />
        <p class="widget-person-card_detail--text">{{ person.vehicle?.licensePlate || 'Unknown' }}</p>
      </div>
    </HeightTransition>
    <Modal v-if="isOpenDeleteModal" v-model="isOpenDeleteModal" hide-close>
      <template #header
        ><p class="title2-bold">Delete {{ getFullName(person.firstName, person.lastName) }}?</p>
      </template>
      <div class="flex h-full w-full flex-col items-start gap-4">
        <p>
          Are you sure you want to delete all the information about
          {{ getFullName(person.firstName, person.lastName) }}?
        </p>
        <p>This action can not be undone</p>
      </div>
      <template #footer>
        <div class="w-full">
          <WButton type="secondary" class="widget-confirm-modal_btn_close" @click="isOpenDeleteModal = false">
            No, keep details
          </WButton>
          <WButton type="primary" class="widget-confirm-modal_btn" @click="deletePerson"> Yes, I am sure </WButton>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
  import { defineAsyncComponent, ref } from 'vue';
  import { ThirdPartyDriver } from 'src/types/overlays';
  import { getFullName } from 'src/utils/helpers';
  import WButton from './Button.vue';
  import HeightTransition from './HeightTransition.vue';
  import { themeIcons } from 'src/utils/config';
  import WidgetIcon from './WidgetIcon.vue';

  const Modal = defineAsyncComponent(() => import('./ConfirmModal.vue'));

  const props = withDefaults(
    defineProps<{
      person: ThirdPartyDriver;
      editIcon: string;
      deleteIcon: string;
    }>(),
    {
      editIcon: themeIcons.value.edit,
      deleteIcon: themeIcons.value.trash,
    }
  );

  const emit = defineEmits<{
    (e: 'edit'): void;
    (e: 'delete'): void;
  }>();

  const isOpenDeleteModal = ref(false);
  const isShowMore = ref(false);
  const icons = {
    driver: themeIcons.value.driver,
    motorcyclist: themeIcons.value.motorcyclist,
    cyclist: themeIcons.value.cyclist,
    pedestrian: themeIcons.value.pedestrian,
    witness: themeIcons.value.witness,
    other: themeIcons.value.other,
  };

  function getFormattedAddress() {
    const address = props.person.address;
    if (!address || Object.values(address).every((value) => !value)) return 'Unknown';
    if (address.formattedAddress) return address.formattedAddress;
    return `${address.addressLine1}, ${address.city}, ${address.state} ${address.postalCode}, ${address.country}`;
  }

  function openDeleteModal() {
    isOpenDeleteModal.value = true;
  }

  function deletePerson() {
    emit('delete');
    isOpenDeleteModal.value = false;
  }
</script>

<style scoped></style>
